import { useStateGetter } from '@eturi/react'
import mapValues from 'lodash/mapValues'
import { useLayoutEffect, useMemo, type ReactNode } from 'react'
import isEqual from 'react-fast-compare'
import { AppResponsiveBrkPt, AppResponsiveBrkPts } from '../../css-variables'
import { ResponsiveContext } from './ResponsiveContext'
import type { AppResponsiveMatches, GetResponsiveValue, ResponsiveCtx } from './types'

export const ResponsiveProvider = ({ children }: { readonly children: ReactNode }) => {
	const [getMatches, setMatches] = useStateGetter<AppResponsiveMatches>(getMediaMatches)

	useLayoutEffect(() => {
		const cleanupFns = AppResponsiveBrkPts.map((bp) => {
			const mediaStr = mediaWidthStr(bp)
			const ql = window.matchMedia(mediaStr)

			const listener = () => {
				const matches = getMediaMatches()
				if (!isEqual(matches, getMatches())) setMatches(matches)
			}

			ql.addEventListener('change', listener)

			return () => {
				ql.removeEventListener('change', listener)
			}
		})

		return () => {
			cleanupFns.forEach((fn) => fn())
		}
	}, [])

	const matches = getMatches()

	const value = useMemo((): ResponsiveCtx => {
		const getResponsiveValue: GetResponsiveValue = (base, map) => {
			for (const size of ORDERED_SIZES) {
				let ret
				if (matches[size] && (ret = map[size]) !== undefined) return ret
			}
			return base
		}

		return Object.freeze({
			fontScaled: (size: number) => getResponsiveValue(BASE_FONT_SIZE, FONT_SCALE) * size,
			getResponsiveValue,
			matches,
		})
	}, [matches])

	return <ResponsiveContext.Provider value={value}>{children}</ResponsiveContext.Provider>
}

const mediaWidthStr = (minBrkPt: number) => `screen and (min-width: ${minBrkPt}px)`

const getMediaMatches = (): AppResponsiveMatches =>
	mapValues(AppResponsiveBrkPt, (v) => window.matchMedia(mediaWidthStr(v)).matches)

const ORDERED_SIZES = ['xxl', 'xl', 'lg', 'md', 'sm'] as const
const BASE_FONT_SIZE = 15 / 16 // xs, sm
const FONT_SCALE = {
	md: 1,
	lg: 17 / 16,
	xl: 18 / 16,
}
